import { apiClient } from './client';

export type CommentEntityType = 'outcome' | 'output' | 'indicator' | 'activity' | 'workplan' | 'beneficiaries' | 'locations' | 'implementation' | 'risk' | 'meal' | 'budget' | 'team' | 'overview' | 'finance' | 'amendments';

export interface Comment {
  id: number;
  project_id: number;
  entity_type: string;
  entity_id: number | null;
  body: string;
  created_by: number;
  created_at: string;
  author_name: string;
}

/** Project-scoped comments - the original, real implementation. Untouched. */
export const commentsApi = {
  list: (projectId: number, entityType: CommentEntityType, entityId?: number) => {
    const params = new URLSearchParams({ projectId: String(projectId), entityType, ...(entityId ? { entityId: String(entityId) } : {}) });
    return apiClient.get<{ data: Comment[] }>(`/api/comments?${params.toString()}`);
  },
  create: (projectId: number, entityType: CommentEntityType, entityId: number | null, body: string) =>
    apiClient.post<{ data: Comment }>('/api/comments', { projectId, entityType, entityId, body }),
};

export interface GenericComment {
  id: number;
  entity_type: string;
  entity_id: number;
  section: string | null;
  parent_comment_id: number | null;
  /** NULL when withdrawn - the server masks the original text; it is never sent to the browser. */
  body: string | null;
  resolution_status: 'none' | 'open' | 'resolved';
  resolved_at: string | null;
  resolved_by_name: string | null;
  withdrawn_at: string | null;
  created_by: number;
  created_at: string;
  author_name: string;
}

export interface CreateCommentOptions { parentCommentId?: number; requiresResolution?: boolean; mentionUserIds?: number[]; }

/**
 * Generic, project-independent comments for HR entities - a
 * genuinely separate real backend endpoint (/api/comments/:entityType/
 * :entityId, not /api/comments?projectId=...), since Project comments
 * require a real project_id and HR entities have nothing to do with
 * Projects. Deliberately NOT merged into commentsApi above to avoid
 * conflating two different real API contracts.
 *
 * `section` (migration 049) matches the OneGMS reference: pass the tab/
 * section key (e.g. 'programming', 'overview') for a record with
 * multiple tabs, so each tab gets its own thread instead of one thread
 * for the whole record. Omit it for a record with no natural tab split.
 */
export const genericCommentApi = {
  list: (entityType: string, entityId: number, section?: string) =>
    apiClient.get<{ data: GenericComment[] }>(`/api/comments/${entityType}/${entityId}${section ? `?section=${encodeURIComponent(section)}` : ''}`),
  create: (entityType: string, entityId: number, body: string, section?: string, options: CreateCommentOptions = {}) =>
    apiClient.post<{ data: { id: number; mentionsSkipped: number[] } }>(`/api/comments/${entityType}/${entityId}${section ? `?section=${encodeURIComponent(section)}` : ''}`, { body, ...options }),
  counts: (entityType: string, entityId: number) =>
    apiClient.get<{ data: { section: string | null; total: number; open: number }[] }>(`/api/comments/${entityType}/${entityId}/counts`),
  resolve: (commentId: number) => apiClient.post<{ data: { id: number } }>(`/api/comments/item/${commentId}/resolve`, {}),
  withdraw: (commentId: number) => apiClient.post<{ data: { id: number } }>(`/api/comments/item/${commentId}/withdraw`, {}),
};
